import React, { useState, useEffect } from 'react';
import { characteristicService } from '../../../services/api';
import { Characteristic, CreateCharacteristicDto, UpdateCharacteristicDto } from '../../../types/categories';

const CharacteristicsPage = () => {
  const [characteristics, setCharacteristics] = useState<Characteristic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCharacteristic, setEditingCharacteristic] = useState<Characteristic | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Form state
  const [formData, setFormData] = useState<CreateCharacteristicDto>({
    name: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    displayOrder: 0
  });

  useEffect(() => {
    loadCharacteristics();
  }, []);

  const loadCharacteristics = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await characteristicService.getAllCharacteristics();
      setCharacteristics(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar características');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCharacteristic(null);
    setFormData({
      name: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      displayOrder: 0
    });
    setIsModalOpen(true);
  };

  const handleEdit = (characteristic: Characteristic) => {
    setEditingCharacteristic(characteristic);
    setFormData({
      name: characteristic.name,
      description: characteristic.description,
      icon: characteristic.icon,
      color: characteristic.color,
      displayOrder: characteristic.displayOrder
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setError('');
      setSuccessMessage('');

      if (editingCharacteristic) {
        const updateData: UpdateCharacteristicDto = {
          ...formData,
          isActive: editingCharacteristic.isActive
        };
        await characteristicService.updateCharacteristic(editingCharacteristic.id, updateData);
        setSuccessMessage('Característica atualizada com sucesso!');
      } else {
        await characteristicService.createCharacteristic(formData);
        setSuccessMessage('Característica criada com sucesso!');
      }

      setIsModalOpen(false);
      await loadCharacteristics();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar característica');
    }
  };

  const handleToggleStatus = async (characteristic: Characteristic) => {
    try {
      setError('');
      const updateData: UpdateCharacteristicDto = {
        name: characteristic.name,
        description: characteristic.description,
        icon: characteristic.icon,
        color: characteristic.color,
        displayOrder: characteristic.displayOrder,
        isActive: !characteristic.isActive
      };
      
      await characteristicService.updateCharacteristic(characteristic.id, updateData);
      setSuccessMessage(`Característica ${characteristic.isActive ? 'desativada' : 'ativada'} com sucesso!`);
      await loadCharacteristics();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao alterar status da característica');
    }
  };

  const handleDelete = async (characteristic: Characteristic) => {
    if (!window.confirm(`Tem certeza que deseja excluir a característica "${characteristic.name}"?`)) {
      return;
    }

    try {
      setError('');
      await characteristicService.deleteCharacteristic(characteristic.id);
      setSuccessMessage('Característica excluída com sucesso!');
      await loadCharacteristics();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao excluir característica');
    }
  };

  // Filtrar características
  const filteredCharacteristics = characteristics.filter(characteristic => {
    const matchesSearch = characteristic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         characteristic.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && characteristic.isActive) ||
                         (statusFilter === 'inactive' && !characteristic.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const renderIcon = (characteristic: Characteristic) => {
    if (characteristic.icon) {
      if (characteristic.icon.startsWith('<svg')) {
        return (
          <div 
            className="w-5 h-5 flex-shrink-0"
            dangerouslySetInnerHTML={{ __html: characteristic.icon }}
            style={{ color: characteristic.color }}
          />
        );
      }
      return (
        <span className="text-lg" style={{ color: characteristic.color }}>
          {characteristic.icon}
        </span>
      );
    }
    return (
      <div 
        className="w-5 h-5 rounded-full flex-shrink-0"
        style={{ backgroundColor: characteristic.color }}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Características</h1>
          <p className="text-gray-600 mt-2">Gerenciar características de estabelecimentos</p>
        </div>
        <div className="flex justify-center py-8">
          <div className="spinner w-8 h-8"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Características</h1>
          <p className="text-gray-600 mt-2">Gerenciar características de estabelecimentos</p>
        </div>
        <button 
          onClick={handleCreate}
          className="btn btn-primary"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Característica
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nome ou descrição..."
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="input"
              >
                <option value="all">Todas</option>
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de características */}
      <div className="card">
        <div className="card-body p-0">
          {filteredCharacteristics.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 text-lg mb-2">
                {searchTerm || statusFilter !== 'all' ? 'Nenhuma característica encontrada' : 'Nenhuma característica cadastrada'}
              </div>
              <p className="text-gray-400">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca' 
                  : 'Clique em "Nova Característica" para começar'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Característica
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCharacteristics.map((characteristic) => (
                    <tr key={characteristic.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {renderIcon(characteristic)}
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {characteristic.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {characteristic.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {characteristic.displayOrder}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          characteristic.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {characteristic.isActive ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(characteristic)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleToggleStatus(characteristic)}
                            className={`p-1 rounded ${
                              characteristic.isActive 
                                ? 'text-orange-600 hover:text-orange-900' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={characteristic.isActive ? 'Desativar' : 'Ativar'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {characteristic.isActive ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              )}
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(characteristic)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsModalOpen(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        {editingCharacteristic ? 'Editar Característica' : 'Nova Característica'}
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nome *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="input"
                            required
                            placeholder="Ex: Wi-Fi Gratuito"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Descrição
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="input"
                            rows={3}
                            placeholder="Descrição da característica..."
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ícone
                            </label>
                            <input
                              type="text"
                              value={formData.icon}
                              onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                              className="input"
                              placeholder="📶 ou emoji"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Cor
                            </label>
                            <input
                              type="color"
                              value={formData.color}
                              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                              className="input h-10"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ordem de Exibição
                          </label>
                          <input
                            type="number"
                            value={formData.displayOrder}
                            onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                            className="input"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="btn btn-primary sm:ml-3 sm:w-auto w-full"
                  >
                    {editingCharacteristic ? 'Atualizar' : 'Criar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto w-full"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacteristicsPage;
